/* eslint-disable i18next/no-literal-string */
import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getBigScriptConfig, IBigScriptConfig } from '../configuration/configuration';
import { useAnalyticsInit } from '../hooks/useAnalyticsInit';
import { updateAnalyticsEnabled } from '../Store/Actions';

interface IAnalyticsProps {
  region?: string;
  language?: string;
}

const BIG_SCRIPT_ID = 'cookies';
const NONCE = 'true';

const upsertMetaTag = (name: string, key: string) => {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.append(meta);
  }
  meta.dataset.key = key;
};

const configureBigScript = (config: IBigScriptConfig) => {
  upsertMetaTag('onetrust-key', config.oneTrust);

  let mParticleMeta = document.head.querySelector<HTMLMetaElement>('meta[name="mparticle-key"]');
  if (!mParticleMeta) {
    mParticleMeta = document.createElement('meta');
    mParticleMeta.name = 'mparticle-key';
    document.head.append(mParticleMeta);
  }
  mParticleMeta.dataset.key = config.mparticle;
  mParticleMeta.dataset.instance_name = 'default_instance';
  mParticleMeta.dataset.plan_id = 'web';
};

export const Analytics: React.FC<IAnalyticsProps> = ({ region, language }) => {
  const dispatch = useDispatch();
  const enableAnalytics = useCallback(() => {
    dispatch(updateAnalyticsEnabled(true));
  }, [dispatch]);

  useAnalyticsInit(enableAnalytics);

  useEffect(() => {
    const configuredScript = getBigScriptConfig();
    const config: IBigScriptConfig = {
      ...configuredScript,
      language: language || configuredScript.language,
      region: region || configuredScript.region
    };

    configureBigScript(config);

    const existingScript = document.querySelector<HTMLScriptElement>(`#${BIG_SCRIPT_ID}`);
    if (existingScript) {
      if (existingScript.dataset.loaded === 'true') {
        enableAnalytics();
      } else {
        const handleLoad = () => {
          existingScript.dataset.loaded = 'true';
          enableAnalytics();
        };
        existingScript.addEventListener('load', handleLoad, { once: true });
        return () => existingScript.removeEventListener('load', handleLoad);
      }
      return undefined;
    }

    const script = document.createElement('script');
    script.id = BIG_SCRIPT_ID;
    script.src = config.scriptUrl;
    script.type = 'module';
    script.dataset.language = config.language;
    script.dataset.region = config.region;
    script.nonce = NONCE;
    const handleLoad = () => {
      script.dataset.loaded = 'true';
      enableAnalytics();
    };
    script.addEventListener('load', handleLoad, { once: true });
    document.head.append(script);

    return () => script.removeEventListener('load', handleLoad);
  }, [enableAnalytics, language, region]);

  // Big Script decorates this anchor; applicationConverter reads it when redirecting to the legacy form.
  return (
    <a id='analytics-params' href='localhost' hidden>
      Analytics parameters
    </a>
  );
};
